import React, { useState, useEffect } from 'react';
import * as Select from '@radix-ui/react-select';
import * as Switch from '@radix-ui/react-switch';
import { ChevronDown, ChevronUp, Check } from 'lucide-react';
import userSettings from '../../utils/UserSettings';
import { 
  aiProviderOptions, 
  getProviderConfig,
  AIConfigField,
  AIProviderConfig
} from '../../services/ai/configs';
import { BaseConfig } from '../../services/ai/types';
import { AIProviderType } from '../../services/ai/AIServiceFactory';

// 定义字段值类型
type FieldValue = string | number | boolean;

// 渲染单个输入字段
const renderField = (
  field: AIConfigField, 
  currentConfig: Record<string, FieldValue>, 
  onChange: (fieldId: string, value: FieldValue) => void, 
  onBlur: () => void
) => {
  const value = currentConfig[field.id] !== undefined 
    ? currentConfig[field.id] 
    : field.defaultValue;
  
  // 处理开关类型
  if (field.type === 'boolean') {
    return (
      <div className="settings-field" key={field.id}>
        <div className="flex items-center justify-between">
          <label className="settings-label mb-0">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <Switch.Root
            checked={Boolean(value)}
            onCheckedChange={(checked) => onChange(field.id, checked)}
            className="w-9 h-5 bg-gray-300 rounded-full relative data-[state=checked]:bg-blue-500 transition-colors"
          >
            <Switch.Thumb className="block w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-100 translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[18px]" />
          </Switch.Root>
        </div>
        {field.description && (
          <p className="text-xs text-gray-500 mt-1">{field.description}</p>
        )}
      </div>
    );
  }
  
  // 处理选择框类型
  if (field.type === 'select' && field.options) {
    return (
      <div className="settings-field" key={field.id}>
        <label className="settings-label">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        
        <Select.Root 
          value={String(value || '')} 
          onValueChange={(newValue) => {
            onChange(field.id, newValue);
            onBlur();
          }}
        >
          <Select.Trigger className="settings-select-trigger" aria-label={field.label}>
            <Select.Value />
            <Select.Icon>
              <ChevronDown className="w-4 h-4 settings-select-icon" />
            </Select.Icon>
          </Select.Trigger>

          <Select.Portal>
            <Select.Content className="rounded-lg shadow-lg border overflow-hidden z-50">
              <Select.ScrollUpButton className="flex items-center justify-center h-6 cursor-default">
                <ChevronUp className="w-4 h-4" />
              </Select.ScrollUpButton>
              
              <Select.Viewport>
                {field.options.map(option => (
                  <Select.Item 
                    key={option.value}
                    value={option.value} 
                    className="flex items-center px-3 py-2 text-sm cursor-pointer outline-none relative"
                  >
                    <Select.ItemText>{option.label}</Select.ItemText>
                    <Select.ItemIndicator className="absolute right-3">
                      <Check className="w-4 h-4" />
                    </Select.ItemIndicator>
                  </Select.Item>
                ))}
              </Select.Viewport>

              <Select.ScrollDownButton className="flex items-center justify-center h-6 cursor-default">
                <ChevronDown className="w-4 h-4" />
              </Select.ScrollDownButton>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
        {field.description && (
          <p className="text-xs text-gray-500 mt-1">{field.description}</p>
        )}
      </div>
    );
  }
  
  // 处理密码类型 (API密钥和敏感信息字段)
  if ((field.id === 'apiKey' || field.id === 'apikey') && field.type === 'text') {
    return (
      <div className="settings-field" key={field.id}>
        <label className="settings-label">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <input
          type="password"
          value={value !== undefined && value !== null ? String(value) : ''}
          onChange={(e) => onChange(field.id, e.target.value)}
          onBlur={onBlur}
          className="settings-input"
          placeholder={field.placeholder}
        />
        {field.description && (
          <p className="text-xs text-gray-500 mt-1">{field.description}</p>
        )}
      </div>
    );
  }
  
  // 处理文本或数字输入框
  return (
    <div className="settings-field" key={field.id}>
      <label className="settings-label">
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <input
        type={field.type === 'number' ? 'number' : 'text'}
        value={value !== undefined && value !== null ? String(value) : ''}
        onChange={(e) => {
          const newValue = field.type === 'number' ? parseFloat(e.target.value) : e.target.value;
          onChange(field.id, newValue);
        }}
        onBlur={onBlur}
        className="settings-input"
        placeholder={field.placeholder}
        step={field.step}
        min={field.min}
        max={field.max}
      />
      {field.description && (
        <p className="text-xs text-gray-500 mt-1">{field.description}</p>
      )}
    </div>
  );
};

const AISettings: React.FC = () => {
  // 状态
  const [aiProvider, setAIProvider] = useState<AIProviderType>(userSettings.getAIProvider());
  const [currentConfig, setCurrentConfig] = useState<BaseConfig & Record<string, string | number | boolean>>({useStreaming: true});
  const [providerConfig, setProviderConfig] = useState<AIProviderConfig | null>(null);
  
  // 当AI提供商变更时，更新当前配置
  useEffect(() => {
    if (aiProvider) {
      const config = userSettings.getAIConfig(aiProvider) || {useStreaming: true};
      setCurrentConfig(config as BaseConfig & Record<string, string | number | boolean>);
      
      // 获取提供商配置
      const providerSchema = getProviderConfig(aiProvider);
      setProviderConfig(providerSchema);
    } else {
      setCurrentConfig({useStreaming: true} as BaseConfig & Record<string, string | number | boolean>);
      setProviderConfig(null);
    }
  }, [aiProvider]);

  // 处理AI提供商变更
  const handleAIProviderChange = (provider: AIProviderType) => {
    setAIProvider(provider);
    userSettings.setAIProvider(provider);
  };

  // 处理配置字段变更
  const handleConfigChange = (fieldId: string, value: string | number | boolean) => {
    setCurrentConfig(prev => ({
      ...prev,
      [fieldId]: value
    } as BaseConfig & Record<string, string | number | boolean>));
    
    // 即时更新配置
    if (aiProvider) {
      userSettings.updateAIConfig(aiProvider, {
        ...currentConfig,
        [fieldId]: value
      });
    }
  };

  // 处理配置完成编辑（失去焦点或提交时）
  const handleConfigBlur = () => {
    if (aiProvider) {
      userSettings.updateAIConfig(aiProvider, currentConfig);
    }
  };
  
  // 渲染流式输出设置
  const renderStreamingOption = () => {
    return (
      <div className="settings-field">
        <div className="flex items-center justify-between">
          <label className="settings-label mb-0">启用流式输出</label>
          <Switch.Root
            checked={currentConfig.useStreaming !== false}
            onCheckedChange={(checked) => {
              const newConfig = {
                ...currentConfig,
                useStreaming: checked
              } as BaseConfig & Record<string, string | number | boolean>;
              setCurrentConfig(newConfig);
              if (aiProvider) {
                userSettings.updateAIConfig(aiProvider, newConfig);
              }
            }}
            className="w-9 h-5 bg-gray-300 rounded-full relative data-[state=checked]:bg-blue-500 transition-colors"
          >
            <Switch.Thumb className="block w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-100 translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[18px]" />
          </Switch.Root>
        </div>
        <p className="text-xs text-gray-500 mt-1">启用后，AI生成的内容将实时显示</p>
      </div>
    );
  };

  // 渲染选择的AI提供商配置
  const renderAIProviderConfig = () => {
    if (!aiProvider || !providerConfig) {
      return <div className="text-sm text-gray-500 mt-2">请先选择一个AI提供商</div>;
    }
    
    return (
      <div className="space-y-3 mt-4">
        {/* 先显示流式输出选项 */}
        {renderStreamingOption()}
        
        {/* 然后逐个显示配置字段 */}
        {providerConfig.fields.map(field => 
          renderField(
            field, 
            currentConfig, 
            handleConfigChange, 
            handleConfigBlur
          )
        )}
      </div>
    );
  };

  return (
    <div className="p-2">
      <h2 className="text-lg font-medium mb-3">AI模型设置</h2>
      
      <div className="settings-field">
        <label className="settings-label">AI提供商</label>
        <Select.Root 
          value={aiProvider || ''} 
          onValueChange={(value) => handleAIProviderChange(value === '' ? null : value as AIProviderType)}
        >
          <Select.Trigger className="settings-select-trigger">
            <Select.Value />
            <Select.Icon>
              <ChevronDown className="w-4 h-4 settings-select-icon" />
            </Select.Icon>
          </Select.Trigger>

          <Select.Portal>
            <Select.Content className="rounded-lg shadow-lg border overflow-hidden z-50">
              <Select.ScrollUpButton className="flex items-center justify-center h-6 cursor-default">
                <ChevronUp className="w-4 h-4" />
              </Select.ScrollUpButton>
              
              <Select.Viewport>
                {aiProviderOptions.map(option => (
                  <Select.Item 
                    key={String(option.value)} 
                    value={String(option.value) || ''} 
                    className="flex items-center px-3 py-2 text-sm cursor-pointer outline-none relative"
                  >
                    <Select.ItemText>{option.label}</Select.ItemText>
                    <Select.ItemIndicator className="absolute right-3">
                      <Check className="w-4 h-4" />
                    </Select.ItemIndicator>
                  </Select.Item>
                ))}
              </Select.Viewport>

              <Select.ScrollDownButton className="flex items-center justify-center h-6 cursor-default">
                <ChevronDown className="w-4 h-4" />
              </Select.ScrollDownButton>
            </Select.Content>
          </Select.Portal>
        </Select.Root>
      </div>
      
      {renderAIProviderConfig()}
      
      <div className="mt-6 text-xs text-gray-500">
        <p>注意：您需要确保已经拥有相应AI模型的访问权限。</p>
        <p>如果您使用的是本地模型（如Ollama或vLLM），确保相应的服务已在本地运行。</p>
      </div>
    </div>
  );
};

export default AISettings;
